import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_RESPONSE_LIMITS, ENDPOINTS } from '@app/core/constants/endpoints';
import { List } from '@app/shared/models/list.model';
import { lastValueFrom, Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AssetGroup } from '@app/shared/models/asset-group.model';
import { AssetGroupAdapter } from './adapters/asset-group.adapter';
import { CountriesStoreService } from '@app/core/stores/countries-store.service';
import { DefaultApiResponse } from '@app/shared/models/default-api-response.model';
import { Shift } from '@app/shared/models/shift.model';
import { cloneDeep, uniqBy } from 'lodash';
import { DeleteAGAssociation } from '@core/utils/asset-group/delete-association';
import { UpdateAGAssociation } from '@core/utils/asset-group/update-association';
import { CreateAGAssociation } from '@core/utils/asset-group/create-association';

@Injectable()
export class AssetGroupService {
    private assetGroupsCount: number;
    private nextAssetGroupId: number;

    constructor(
        private http: HttpClient,
        private adapter: AssetGroupAdapter,
        private countriesStoreService: CountriesStoreService
    ) {}

    getAssetGroupsFromJsonTree(companyId: number): Observable<List<AssetGroup>> {
        return this.http
            .get(`${ENDPOINTS.apis.companies}/${companyId}/assetgroupshierarchytree`)
            .pipe(
                map((response: any) => {
                    this.assetGroupsCount = 0;
                    this.nextAssetGroupId = response.nextAssetGroupId;

                    let rawTree = JSON.parse(response.jsonTree);
                    if (Object.keys(rawTree).includes('jsonTree')) {
                        rawTree = JSON.parse(rawTree.jsonTree);
                    }

                    const list = [];

                    rawTree.forEach((current) => {
                        list.push(this.adaptAssetGroupFromJsonTree(current));
                    });

                    return {
                        start: 0,
                        limit: this.assetGroupsCount,
                        size: this.assetGroupsCount,
                        links: {
                            previous: '',
                            next: '',
                        },
                        items: list,
                    } as List<AssetGroup>;
                })
            );
    }

    getCompanyAssetGroups(companyId: number): Observable<List<AssetGroup>> {
        const params = new HttpParams()
            .set('verbose', String(true))
            .set('limit', API_RESPONSE_LIMITS.default.toString());
        return this.http
            .get(`${ENDPOINTS.apis.companies}/${companyId}/assetgroups`, { params })
            .pipe(
                map((list: any) => {
                    return {
                        start: list.start,
                        limit: list.limit,
                        size: list.size,
                        links: {
                            previous: list.links.previous,
                            next: list.links.next,
                        },
                        items: list.objects.map((data: any) => {
                            data.country = this.countriesStoreService.getCountry(
                                data.country.countryCode
                            );

                            return this.adapter.adapt(data);
                        }),
                    } as List<AssetGroup>;
                })
            );
    }

    getSingle(companyId: number, id: number): Observable<AssetGroup> {
        return this.http.get(`${ENDPOINTS.apis.companies}/${companyId}/assetgroups/${id}`).pipe(
            map((data: any) => {
                if (data.country) {
                    data.country = this.countriesStoreService.getCountry(data.country.countryCode);
                }

                return this.adapter.adapt(data);
            })
        );
    }

    async create(companyId: number, assetGroup: AssetGroup): Promise<AssetGroup> {
        try {
            const adaptedAssetGroup = this.adaptAPIAssetGroup(assetGroup);
            const currentTree = await lastValueFrom(this.getAssetGroupsFromJsonTree(companyId));
            const originalList = this.getFlatList(currentTree.items);
            const updatedList = cloneDeep(originalList);

            updatedList.push({
                ...assetGroup,
                id: this.nextAssetGroupId,
            });
            adaptedAssetGroup.jsonTree = this.getJSONHierarchy(updatedList);

            const response = await lastValueFrom(
                this.adaptAssetGroupObservable(
                    this.http.post(
                        `${ENDPOINTS.apis.companies}/${companyId}/assetgroups`,
                        adaptedAssetGroup
                    )
                ).pipe(
                    switchMap((group) => {
                        const createAGAssociation = new CreateAGAssociation(
                            this.http,
                            group,
                            originalList,
                            updatedList
                        );
                        return createAGAssociation.update(companyId).pipe(map(() => group));
                    })
                )
            );

            return Promise.resolve(response);
        } catch {
            return Promise.reject();
        }
    }

    async update(companyId: number, assetGroup: AssetGroup): Promise<AssetGroup> {
        try {
            const currentTree = await lastValueFrom(this.getAssetGroupsFromJsonTree(companyId));
            const originalList = this.getFlatList(currentTree.items);
            const updatedList = cloneDeep(originalList);
            const editingGroup = updatedList.find((group) => group.id === assetGroup.id);

            Object.assign(editingGroup, {
                ...assetGroup,
            });
            this.updateChildren(editingGroup, updatedList);

            const adaptedAssetGroup = this.adaptAPIAssetGroup(editingGroup);
            adaptedAssetGroup.jsonTree = this.getJSONHierarchy(updatedList);

            const response = await lastValueFrom(
                this.adaptAssetGroupObservable(
                    this.http.put(
                        `${ENDPOINTS.apis.companies}/${companyId}/assetgroups/${assetGroup.id}`,
                        adaptedAssetGroup
                    )
                ).pipe(
                    switchMap((group) => {
                        const updateAGAssociation = new UpdateAGAssociation(
                            this.http,
                            group,
                            originalList,
                            updatedList
                        );
                        return updateAGAssociation.update(companyId).pipe(map(() => group));
                    })
                )
            );

            return Promise.resolve(response);
        } catch {
            return Promise.reject();
        }
    }

    async delete(companyId: number, assetGroup: AssetGroup): Promise<DefaultApiResponse> {
        try {
            const list = await lastValueFrom(this.getAssetGroupsFromJsonTree(companyId));
            const originalList = this.getFlatList(list.items);
            const updatedList = cloneDeep(originalList);

            updatedList.forEach((item) => {
                if (item.parentId === assetGroup.id) {
                    item.parentId = assetGroup.parentId;
                }
            });

            updatedList.splice(
                updatedList.findIndex((item) => item.id === assetGroup.id),
                1
            );

            const updatedTree = this.getHierarchy(updatedList);
            const jsonTree = this.getJSONHierarchy(updatedTree);
            const deleteAGAssociation = new DeleteAGAssociation(
                this.http,
                assetGroup,
                originalList,
                updatedList
            );

            const response = await lastValueFrom(
                deleteAGAssociation.update(companyId).pipe(
                    switchMap(() => {
                        return this.http
                            .request(
                                'delete',
                                `${ENDPOINTS.apis.companies}/${companyId}/assetgroups/${assetGroup.id}`,
                                { body: jsonTree, responseType: 'text' }
                            )
                            .pipe(map((result) => ({ succeed: result.toLowerCase() === 'ok' })));
                    })
                )
            );

            return Promise.resolve(response);
        } catch {
            return Promise.reject();
        }
    }

    async updateHierarchyShift(
        companyId: number,
        shift: Shift,
        removeShift = false
    ): Promise<void> {
        const hierarchy = await lastValueFrom(this.getAssetGroupsFromJsonTree(companyId));
        const flatList = this.getFlatList(hierarchy.items);

        flatList.forEach((group) => {
            if (group.shifts.ids.includes(shift.id)) {
                if (removeShift) {
                    group.shifts.list = group.shifts.list.filter(
                        (current) => current.id !== shift.id
                    );
                    group.shifts.ids = group.shifts.ids.filter((current) => current !== shift.id);
                } else {
                    const editingShiftIndex = group.shifts.list.findIndex(
                        (item) => item.id === shift.id
                    );

                    group.shifts.list[editingShiftIndex] = {
                        ...shift,
                    };
                }
            }
        });

        const json = this.getJSONHierarchy(hierarchy.items);

        await lastValueFrom(
            this.http.put(`${ENDPOINTS.apis.companies}/${companyId}/assetgroupshierarchytree`, {
                jsonTree: json,
            })
        );
    }

    private updateChildren(currentAssetGroup: AssetGroup, list: AssetGroup[]) {
        const flatList = this.getFlatList(list);
        const children = flatList.filter((item) => item.parentId === currentAssetGroup.id);
        children.forEach((child) => {
            if (child.contact.inherited) {
                child.contact = {
                    ...currentAssetGroup.contact,
                    inherited: child.contact.inherited,
                };
            }

            if (child.location.inherited) {
                child.location = {
                    ...currentAssetGroup.location,
                    inherited: child.location.inherited,
                };
            }

            if (child.shifts.inherited) {
                child.shifts = {
                    ...currentAssetGroup.shifts,
                    inherited: child.shifts.inherited,
                };
            }

            if (child.timezone.inherited) {
                child.timezone = {
                    ...currentAssetGroup.timezone,
                    inherited: child.timezone.inherited,
                };
            }

            this.updateChildren(child, list);
        });
    }

    getFlatList(assetGroups: AssetGroup[]): AssetGroup[] {
        return assetGroups.flatMap((current: AssetGroup) => [
            { ...current, children: [] },
            ...this.getFlatList(current.children),
        ]);
    }

    /* eslint-disable @typescript-eslint/no-unsafe-return */
    private adaptChildrenFromJsonTree(children: any[]) {
        return children.map((child) => {
            return this.adaptAssetGroupFromJsonTree(child);
        });
    }

    private adaptAssetGroupFromJsonTree(rawAssetGroup: any) {
        this.assetGroupsCount++;
        return {
            ...this.adapter.adapt(rawAssetGroup),
            children: this.adaptChildrenFromJsonTree(
                rawAssetGroup.childnodes ?? rawAssetGroup.nodes ?? []
            ),
        };
    }

    private adaptAssetGroupObservable(data$: Observable<any>): Observable<AssetGroup> {
        return data$.pipe(
            map((data: any) => {
                if (data.country) {
                    data.country = this.countriesStoreService.getCountry(data.country.countryCode);
                }

                return this.adapter.adapt(data);
            })
        );
    }

    private adaptAPIAssetGroup(assetGroup: AssetGroup): any {
        return {
            groupType: assetGroup.type,
            groupValue: assetGroup.name,
            contactName: assetGroup.contact.name,
            contactPhone: assetGroup.contact.phone,
            address: assetGroup.location.address,
            city: assetGroup.location.city,
            state: assetGroup.location.state,
            zip: assetGroup.location.zip,
            country: assetGroup.location.country.code,
            contactIsInherited: assetGroup.contact.inherited,
            locationIsInherited: assetGroup.location.inherited,
            shiftListIsInherited: assetGroup.shifts.inherited,
            shifts: assetGroup.shifts.ids,
            timezoneIsInherited: assetGroup.timezone.inherited,
            timezone: assetGroup.timezone.code || null,
            parentGroupId: assetGroup.parentId,
        };
    }

    private adaptJsonAssetGroup(group: AssetGroup): any {
        return {
            ...this.adaptAPIAssetGroup(group),
            id: group.id,
            groupValueContactName: group.contact.name,
            groupValueContactPhone: group.contact.phone,
            groupValueAddress: group.location.address,
            groupValueCity: group.location.city,
            groupValueState: group.location.state,
            groupValueZip: group.location.zip,
            country: {
                name: group.location.country.name,
                countryCode: group.location.country.code,
            },
            shifts: group.shifts.list.map((shift) => this.adaptJsonShift(shift)),
            timezone: group.timezone.code,
        };
    }

    private adaptJsonShift(shift: Shift): any {
        return {
            id: shift.id,
            name: shift.name,
            description: shift.description,
            startTime: shift.startTime,
            duration: shift.durationHs,
            days: shift.daysOfWeek,
        };
    }

    private getHierarchy(assetGroups: AssetGroup[]): AssetGroup[] {
        const groups = assetGroups.filter((item) => !item.parentId);
        const flatList = this.getFlatList(assetGroups);

        groups.forEach((group) => {
            group.children = this.getChildren(flatList, group.id);
        });

        return groups;
    }

    private getChildren(groups: AssetGroup[], id: number): any[] {
        return uniqBy(
            groups
                .filter((group) => group.parentId === id)
                .map((group) => {
                    return {
                        ...group,
                        children: this.getChildren(groups, group.id),
                    };
                }),
            'id'
        );
    }

    private getJSONHierarchy(assetGroups: AssetGroup[]): string {
        const groups = assetGroups
            .filter((item) => !item.parentId)
            .map((group) => this.adaptJsonAssetGroup(group));
        const flatList = this.getFlatList(assetGroups);

        groups.forEach((group) => {
            group.childnodes = this.getChildrenNodes(flatList, group.id);
        });

        return JSON.stringify(groups);
    }

    private getChildrenNodes(groups: AssetGroup[], id: number): any[] {
        return uniqBy(
            groups
                .filter((group) => group.parentId === id)
                .map((group) => {
                    return {
                        ...this.adaptJsonAssetGroup(group),
                        childnodes: this.getChildrenNodes(groups, group.id),
                    };
                }),
            'id'
        );
    }
    /* eslint-enable @typescript-eslint/no-unsafe-return */
}
